import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, onSnapshot, query, writeBatch, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { disciplineService } from '../services/disciplineService';
import { subscribeRecognitionRules } from '../services/recognitionService';
import { useAuth } from '../features/auth/hooks/useAuth';
import { useSchoolFirestore } from './useSchoolFirestore';
import { useSchool } from '../contexts/SchoolContext';
import {
  getGovernedDomainRuleSetIssues,
  validateGovernedDomainRuleSet,
} from '../services/domain/domainRuleValidation';
import { dynamicDomainListConstraints } from '../services/domain/dynamicDomainQueries';
import { commitDomainRuleConfiguration, newDomainRuleId } from '../services/domainRuleConfiguration';

/**
 * Resolve the Firestore collection name to use for rules based on context.
 *  - If a domain is provided: use domain.collections.rules
 *  - Else if recognition mode: 'recognitionRules' (legacy)
 *  - Else: 'disciplineRules' (legacy)
 */
function resolveCollectionName({ domain, isRecognitionMode }) {
  const domainCollection = domain?.collections?.rules || domain?.rulesCollection;
  if (domainCollection) return domainCollection;
  return isRecognitionMode ? 'recognitionRules' : 'disciplineRules';
}

export const useDisciplineRules = ({ isRecognitionMode = false, domain = null } = {}) => {
  const collectionName = resolveCollectionName({ domain, isRecognitionMode });
  const configurationDomainId = domain?.id || (isRecognitionMode ? 'recognition' : 'discipline');
  const serverConfigured = Boolean(domain && /^domain_[a-z0-9_]+_rules$/.test(collectionName))
    || ['disciplineRules', 'recognitionRules'].includes(collectionName)
    || configurationDomainId === 'academics' && collectionName === 'academicsRules';
  const savedRevisionsRef = useRef(new Map());
  // School-scoped CRUD operations
  const { create: createWithSchool, update: updateWithSchool, remove: removeWithSchool } = useSchoolFirestore();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadRevision, setLoadRevision] = useState(0);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const lastFailedSaveRef = useRef(null);
  const subscriptionIdentityRef = useRef(null);
  const { user } = useAuth();
  const { schoolId, loading: schoolLoading } = useSchool();

  // Subscribe to rules changes — path depends on domain/mode
  useEffect(() => {
    if (schoolLoading) return;

    if (!schoolId) {
      setRules([]);
      setLoading(false);
      setLoadError(null);
      return;
    }

    const subscriptionIdentity = `${schoolId}:${collectionName}:${domain?.id || ''}`;
    if (subscriptionIdentityRef.current !== subscriptionIdentity) {
      subscriptionIdentityRef.current = subscriptionIdentity;
      setRules([]);
    }
    setLoading(true);
    setLoadError(null);
    let unsubscribe;

    try {
      // Legacy protected collections go through their service (keeps any custom
      // normalization the services do). Any other collection name is a domain
      // collection and is subscribed generically.
      const isLegacyDiscipline = collectionName === 'disciplineRules';
      const isLegacyRecognition = collectionName === 'recognitionRules';

      if (isLegacyDiscipline || isLegacyRecognition) {
        const subscribeFn = isLegacyRecognition
          ? subscribeRecognitionRules
          : disciplineService.subscribeDisciplineRules;
        unsubscribe = subscribeFn((newRules) => {
          try {
            setRules((newRules || []).filter(rule => rule.retired !== true));
            setLoading(false);
            setLoadError(null);
          } catch (err) {
            console.error('[useDisciplineRules] Error processing rules:', err);
            setLoadError(err.message || 'Rules could not be loaded');
            setLoading(false);
          }
        }, { schoolId });
      } else {
        // Generic domain rules subscription. We deliberately do NOT use a
        // Firestore orderBy('sortOrder') here: an ordered query silently
        // EXCLUDES documents that lack the ordered field, so a rule saved
        // without a sortOrder (e.g. a freshly-created score rule) would never
        // appear in the list even though the write succeeded. Fetch unordered
        // and sort client-side — matching the repo's "sort client-side" rule.
        const colRef = collection(db, 'schools', schoolId, collectionName);
        const rulesQuery = query(
          colRef,
          ...dynamicDomainListConstraints({
            collectionName,
            schoolId,
            domainId: domain?.id,
          }),
        );
        unsubscribe = onSnapshot(
          rulesQuery,
          (snap) => {
            const docs = snap.docs
              .map((d) => ({ ...d.data(), id: d.id }))
              .filter((rule) => rule.retired !== true);
            docs.sort((a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER));
            setRules(docs);
            setLoading(false);
            setLoadError(null);
          },
          (err) => {
            console.error('[useDisciplineRules] Domain subscribe failed:', err);
            setLoadError(err.message || 'Rules could not be loaded');
            setLoading(false);
          }
        );
      }
    } catch (err) {
      console.error('[useDisciplineRules] Error setting up subscription:', err);
      setLoadError(err.message || 'Rules could not be loaded');
      setLoading(false);
    }

    return () => {
      try {
        if (unsubscribe && typeof unsubscribe === 'function') {
          unsubscribe();
        }
      } catch (err) {
        console.error('[useDisciplineRules] Error unsubscribing:', err);
      }
    };
  }, [schoolId, schoolLoading, collectionName, domain?.id, loadRevision]);

  const retryLoad = useCallback(() => {
    setLoadRevision((revision) => revision + 1);
  }, []);

  const invalidRules = useMemo(() => {
    if (!domain) return [];
    return getGovernedDomainRuleSetIssues(rules, domain);
  }, [domain, rules]);

  const invalidRuleIds = useMemo(
    () => new Set(invalidRules.map((issue) => issue.ruleId).filter(Boolean)),
    [invalidRules],
  );

  // Compute statistics
  const stats = useMemo(() => {
    const total = rules.length;
    const active = rules.filter(rule => rule.active).length;
    const inactive = total - active;

    const byCategory = {};
    rules.forEach(rule => {
      const category = rule.type === 'response' ? 'mention' : (rule.type || 'general');
      if (!byCategory[category]) {
        byCategory[category] = { total: 0, active: 0, inactive: 0, invalid: 0 };
      }
      byCategory[category].total++;
      if (rule.active) {
        byCategory[category].active++;
      } else {
        byCategory[category].inactive++;
      }
      if (invalidRuleIds.has(rule.id)) byCategory[category].invalid++;
    });

    return { total, active, inactive, invalid: invalidRules.length, byCategory };
  }, [invalidRuleIds, invalidRules.length, rules]);

  // Memoize rules grouped by type to prevent re-renders
  const rulesByType = useMemo(() => {
    const grouped = {};
    rules.forEach(rule => {
      const type = rule.type === 'response' ? 'mention' : rule.type;
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(rule);
    });
    return grouped;
  }, [rules]);

  // Get rules by type (returns memoized array)
  const getRulesByType = useCallback((type) => {
    return rulesByType[type] || [];
  }, [rulesByType]);

  // Save rule with proper status handling
  const saveRule = useCallback(async (ruleData, existingId = null, request = {}) => {
    setSaveStatus('saving');
    setError(null);
    const domainIdentity = domain
      ? { schoolId, domainId: domain.id }
      : {};
    const safeRuleData = { ...ruleData };
    delete safeRuleData.id;
    const saved = rules.find(rule => rule.id === existingId);
    const expectedRevision = request.expectedRevision ?? Math.max(saved?.configurationRevision || 0,
      savedRevisionsRef.current.get(`${schoolId}:${configurationDomainId}:${existingId}`) || 0);
    const writeId = request.writeId || existingId || (serverConfigured ? newDomainRuleId() : null);

    try {
      const candidate = {
        ...(existingId ? saved : {}),
        ...safeRuleData,
        ...(existingId ? { id: existingId } : {}),
        ...domainIdentity,
      };
      if (domain && candidate.active === true) {
        const projectedRules = [
          ...rules.filter((rule) => rule.id !== existingId),
          candidate,
        ];
        validateGovernedDomainRuleSet(projectedRules, domain);
      }

      let savedRule;

      if (serverConfigured) {
        const receipt = await commitDomainRuleConfiguration({
          operation: existingId ? 'update' : 'create', schoolId, domainId: configurationDomainId,
          ruleId: writeId, configuration: { ...safeRuleData,
            ...(!existingId ? { sortOrder: safeRuleData.sortOrder ?? (rules.length + 1) * 1000 } : {}) },
          ...(existingId ? { expectedRevision } : {}),
        });
        savedRevisionsRef.current.set(`${schoolId}:${configurationDomainId}:${receipt.ruleId}`, receipt.configurationRevision);
        savedRule = { ...saved, ...safeRuleData, id: receipt.ruleId, configurationRevision: receipt.configurationRevision };
      } else if (existingId) {
        // The document path already supplies its stable identity. A newly
        // created rule has no embedded id; adding one during an edit violates
        // the same immutable-identity boundary that protects its history.
        await updateWithSchool(collectionName, existingId, {
          ...safeRuleData,
          ...domainIdentity,
          updatedBy: user?.uid
        });
        savedRule = { ...safeRuleData, id: existingId };
      } else {
        // Stamp a sortOrder on creation so the rule has a stable position and
        // is never at the mercy of a missing-field ordering query. New rules
        // land at the end of the current list.
        const nextSortOrder = (rules.length + 1) * 1000;
        const ruleId = await createWithSchool(collectionName, {
          ...safeRuleData,
          ...domainIdentity,
          sortOrder: safeRuleData.sortOrder ?? nextSortOrder,
          createdBy: user?.uid
        });
        savedRule = { ...safeRuleData, id: ruleId };
      }

      setSaveStatus('saved');
      lastFailedSaveRef.current = null;
      setTimeout(() => setSaveStatus('idle'), 2000);
      return savedRule;
    } catch (err) {
      console.error('Error saving rule:', err);
      lastFailedSaveRef.current = { ruleData, existingId, request: { writeId, expectedRevision } };
      setError(err.message || 'Failed to save rule');
      setSaveStatus('error');
      throw err;
    }
  }, [user, schoolId, domain, configurationDomainId, collectionName, serverConfigured, createWithSchool, updateWithSchool, rules]);

  const retryLastSave = useCallback(async () => {
    const failed = lastFailedSaveRef.current;
    if (!failed) return null;
    return saveRule(failed.ruleData, failed.existingId, failed.request);
  }, [saveRule]);

  // Retire a Domain rule. Its stable id is part of deterministic
  // rule-fire and Queue history, so it must remain available to historical
  // readers, including Behaviour and Recognition's existing collections.
  const deleteRule = useCallback(async (ruleId) => {
    setSaveStatus('saving');
    setError(null);

    try {
      if (serverConfigured) {
        const saved = rules.find(rule => rule.id === ruleId);
        const expectedRevision = Math.max(saved?.configurationRevision || 0,
          savedRevisionsRef.current.get(`${schoolId}:${configurationDomainId}:${ruleId}`) || 0);
        await commitDomainRuleConfiguration({ operation: 'retire', schoolId, domainId: configurationDomainId, ruleId, expectedRevision });
      } else if (domain) {
        await updateWithSchool(collectionName, ruleId, {
          active: false,
          retired: true,
          retiredBy: user?.uid,
          schoolId,
          domainId: domain.id,
        });
      } else {
        await removeWithSchool(collectionName, ruleId);
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Error deleting rule:', err);
      setError(err.message || 'Failed to delete rule');
      setSaveStatus('error');
      throw err;
    }
  }, [collectionName, domain, configurationDomainId, serverConfigured, rules, removeWithSchool, schoolId, updateWithSchool, user?.uid]);

  // Duplicate rule
  const duplicateRule = useCallback(async (ruleData) => {
    const duplicatedData = {
      ...ruleData,
      name: `${ruleData.name} (Copy)`,
      id: undefined,
      createdAt: undefined,
      updatedAt: undefined
    };
    return await saveRule(duplicatedData);
  }, [saveRule]);

  // Batched sort-order update — works for any collection
  const updateSortOrders = useCallback(async (updates) => {
    if (!schoolId || !Array.isArray(updates) || updates.length === 0) return;
    try {
      if (serverConfigured) {
        const receipt = await commitDomainRuleConfiguration({ operation: 'reorder', schoolId, domainId: configurationDomainId,
          orders: updates.map(({ id, sortOrder }) => ({ id, sortOrder,
            expectedRevision: Math.max(rules.find(rule => rule.id === id)?.configurationRevision || 0,
              savedRevisionsRef.current.get(`${schoolId}:${configurationDomainId}:${id}`) || 0) })) });
        receipt.rules.forEach(rule => savedRevisionsRef.current.set(`${schoolId}:${configurationDomainId}:${rule.ruleId}`, rule.configurationRevision));
        return;
      }
      const batch = writeBatch(db);
      updates.forEach(({ id, sortOrder }) => {
        const ref = doc(db, 'schools', schoolId, collectionName, id);
        batch.update(ref, { sortOrder });
      });
      await batch.commit();
    } catch (err) {
      console.error('Error updating sort orders:', err);
      throw err;
    }
  }, [schoolId, collectionName, serverConfigured, configurationDomainId, rules]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
    lastFailedSaveRef.current = null;
    if (saveStatus === 'error') {
      setSaveStatus('idle');
    }
  }, [saveStatus]);

  return {
    // Data
    rules,
    loading,
    loadError,
    error,
    saveStatus,
    stats,
    invalidRules,

    // Actions
    saveRule,
    deleteRule,
    duplicateRule,
    updateSortOrders,
    retryLoad,
    retryLastSave,
    clearError,
    getRulesByType
  };
};

// Separate UI state hook for form and section management
export const useDisciplineRulesUI = () => {
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  const [visibleForms, setVisibleForms] = useState(new Set());

  const toggleSection = useCallback((sectionKey) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionKey)) {
        newSet.delete(sectionKey);
      } else {
        newSet.add(sectionKey);
      }
      return newSet;
    });
  }, []);

  const showForm = useCallback((categoryKey) => {
    setVisibleForms(prev => new Set(prev).add(categoryKey));
  }, []);

  const hideForm = useCallback((categoryKey) => {
    setVisibleForms(prev => {
      const newSet = new Set(prev);
      newSet.delete(categoryKey);
      return newSet;
    });
  }, []);

  const isFormVisible = useCallback((categoryKey) => {
    return visibleForms.has(categoryKey);
  }, [visibleForms]);

  const isSectionCollapsed = useCallback((sectionKey) => {
    return collapsedSections.has(sectionKey);
  }, [collapsedSections]);

  return {
    toggleSection,
    showForm,
    hideForm,
    isFormVisible,
    isSectionCollapsed
  };
};
